import { LoadingButton } from '@mui/lab';
import {
    Button,
    Card,
    CardContent,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import server from '../../../services/server';
import useErrors from '../../../hooks/useErrors';

const ADVERTISER_USER_ROLES = [
    { label: 'Proprietário', value: 'owner' },
    { label: 'Administrador', value: 'admin' },
    { label: 'Gerente', value: 'manager' },
    { label: 'Atendente', value: 'user' },
];

const userSchema = yup.object().shape({
    userName: yup.string().when('isEditing', {
        is: true,
        then: schema => schema.required('Informe o usuário'),
        otherwise: schema => schema.trim().email('Informe um e-mail válido').required('Informe o e-mail'),
    }),
    role: yup.string().required(),
});

const FORM_ID_DIALOG = 'advertiser-user-assignment-form-dialog';
const FORM_ID_INLINE = 'advertiser-user-assignment-form-inline';

/**
 * Formulário de vínculo usuário/senha do anunciante (POST/PUT `/advertiser/users`).
 * `variant="dialog"`: como modal (lista em `/anunciante/usuarios`).
 * `variant="inline"`: card na aba em Dados do anunciante.
 */
export default function AdvertiserUserAssignmentForm({
    assignment = null,
    onSuccess,
    variant = 'inline',
    open = false,
    onClose,
}) {
    const { setErrors, enqueueSnackbar } = useErrors();
    const formId = variant === 'dialog' ? FORM_ID_DIALOG : FORM_ID_INLINE;

    const formik = useFormik({
        enableReinitialize: true,
        validationSchema: userSchema,
        initialValues: {
            userName: assignment?.user?.userName || '',
            password: '',
            role: assignment?.role || 'user',
            status: typeof assignment?.status === 'boolean' ? assignment.status : true,
            isEditing: Boolean(assignment),
        },
        onSubmit: async values => {
            try {
                const payload = {
                    role: values.role,
                };

                if (assignment?.id) {
                    await server.put(`/advertiser/users/${assignment.id}`, payload);
                } else {
                    await server.post('/advertiser/invites', {
                        email: values.userName.trim(),
                        role: values.role === 'owner' ? 'user' : values.role,
                    });
                }

                await onSuccess?.();
                enqueueSnackbar(assignment?.id ? 'Usuário atualizado.' : 'Convite enviado.');
                if (variant === 'dialog') {
                    onClose?.();
                }
            } catch (error) {
                setErrors(error);
            }
        },
    });

    const fields = (
        <Stack spacing={2} pt={variant === 'dialog' ? 1 : 0} component="form" id={formId} onSubmit={formik.handleSubmit}>
            <TextField
                label={assignment ? 'Usuário' : 'E-mail'}
                name="userName"
                type={assignment ? 'text' : 'email'}
                value={formik.values.userName}
                onChange={formik.handleChange}
                fullWidth
                disabled={Boolean(assignment)}
                error={formik.touched.userName && Boolean(formik.errors.userName)}
                helperText={
                    (formik.touched.userName && formik.errors.userName) ||
                    (!assignment ? 'O convite é enviado para este e-mail. Se a conta já existir, ela só ganha o vínculo.' : '')
                }
            />
            <TextField select label="Permissão" name="role" value={formik.values.role} onChange={formik.handleChange} fullWidth>
                {ADVERTISER_USER_ROLES.filter(role => assignment || role.value !== 'owner').map(role => (
                    <MenuItem key={role.value} value={role.value}>
                        {role.label}
                    </MenuItem>
                ))}
            </TextField>
            {variant === 'inline' && (
                <Stack direction="row" justifyContent="flex-end">
                    <LoadingButton type="submit" variant="contained" loading={formik.isSubmitting}>
                        Salvar
                    </LoadingButton>
                </Stack>
            )}
        </Stack>
    );

    if (variant === 'dialog') {
        return (
            <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
                <DialogTitle>{assignment ? 'Editar usuário' : 'Convidar usuário'}</DialogTitle>
                <DialogContent dividers>{fields}</DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>Cancelar</Button>
                    <Button type="submit" form={formId} variant="contained" disabled={formik.isSubmitting}>
                        {assignment ? 'Salvar' : 'Enviar convite'}
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }

    return (
        <Card>
            <CardContent>
                <Typography variant="h6" gutterBottom>
                    {assignment ? 'Permissão' : 'Convidar usuário'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {assignment
                        ? 'Altere só o cargo deste acesso. Senha e status não mudam por aqui; a recuperação de senha continua no fluxo de e-mail, e o admin Eurobem pode redefinir a senha.'
                        : 'Envia um convite por e-mail. Se a pessoa já tiver conta Eurobem, ela aceita com a senha atual e ganha o vínculo com este anunciante.'}
                </Typography>
                {fields}
            </CardContent>
        </Card>
    );
}
